import posthog from 'posthog-js';

/**
 * Global fetch interceptor for automatic PostHog API logging
 * This intercepts all fetch calls without modifying existing code
 */

let isInterceptorActive = false;

export const initializeFetchInterceptor = () => {
  if (isInterceptorActive) {
    console.log('Fetch interceptor already active');
    return;
  }

  if (typeof window === 'undefined') {
    console.log('Fetch interceptor: Window not available');
    return;
  }

  // Store the original fetch function
  const originalFetch = window.fetch;

  // Override the global fetch function
  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const [url, options = {}] = args;
    const startTime = Date.now();
    const method = options.method || 'GET';
    
    // Extract URL string if it's a Request object
    const urlString = typeof url === 'string' ? url : url.toString();
    
    // Extract request body for logging
    const requestBody = options.body ? (typeof options.body === 'string' ? options.body.substring(0, 1000) : '[Binary/FormData]') : null;
    
    // Skip logging for non-API calls (static assets, etc.)
    if (!urlString.includes('/api/') && !urlString.includes('localhost:8080')) {
      return originalFetch(...args);
    }

    try {
      const response = await originalFetch(...args);
      const duration = Date.now() - startTime;
      
      // Check if response is successful (2xx status codes)
      if (response.ok) {
        // Log API call success
        posthog.capture('api_call_success', {
          'data-mg-api-method': method,
          'data-mg-api-url': urlString,
          'data-mg-api-status': response.status,
          'data-mg-api-body': requestBody,
          'data-mg-api-duration-ms': duration
        });
      } else {
        // Log API call failure for HTTP error responses (4xx, 5xx)
        posthog.capture('api_call_failure', {
          'data-mg-api-method': method,
          'data-mg-api-url': urlString,
          'data-mg-api-status': response.status,
          'data-mg-api-body': requestBody,
          'data-mg-api-duration-ms': duration
        });
      }

      return response;
    } catch (error) {
      const duration = Date.now() - startTime;
      
      // Log API call failure for network errors, timeouts, etc.
      posthog.capture('api_call_failure', {
        'data-mg-api-method': method,
        'data-mg-api-url': urlString,
        'data-mg-api-status': 0, // Network error
        'data-mg-api-body': requestBody,
        'data-mg-api-duration-ms': duration
      });

      throw error;
    }
  };

  isInterceptorActive = true;
};

export const disableFetchInterceptor = () => {
  if (!isInterceptorActive) {
    return;
  }

  // Note: This is a simplified approach. In a real scenario, you'd need to store
  // the original fetch reference to restore it properly
  isInterceptorActive = false;
};

export const isFetchInterceptorActive = () => isInterceptorActive;

